/**
 * Files attached to mail sent from the shared inbox. Safe to import in the
 * browser: the composer checks the same limits before uploading.
 */

/** Gmail's own limit for the attachments on one email. */
export const MAX_ATTACHMENTS_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;

/**
 * File types Gmail refuses to send, as executable content; checked here so
 * the user hears why before the send fails.
 * https://support.google.com/mail/answer/6590
 */
const BLOCKED_EXTENSIONS = new Set([
  "ade", "adp", "apk", "appx", "appxbundle", "bat", "cab", "chm", "cmd",
  "com", "cpl", "dll", "dmg", "ex", "ex_", "exe", "hta", "ins", "isp",
  "iso", "jar", "js", "jse", "lib", "lnk", "mde", "msc", "msi", "msix",
  "msixbundle", "msp", "mst", "nsh", "pif", "ps1", "scr", "sct", "shb",
  "sys", "vb", "vbe", "vbs", "vxd", "wsc", "wsf", "wsh",
]);

export type AttachmentInfo = { name: string; size: number };

const extension = (name: string): string =>
  name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";

/** Why these files can't be sent, or null when they can. */
export function attachmentProblem(files: AttachmentInfo[]): string | null {
  if (files.length > MAX_ATTACHMENTS) {
    return `Attach at most ${MAX_ATTACHMENTS} files`;
  }
  const blocked = files.find((f) => BLOCKED_EXTENSIONS.has(extension(f.name)));
  if (blocked) {
    return `${blocked.name} can't be sent: Gmail blocks .${extension(blocked.name)} files`;
  }
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_ATTACHMENTS_BYTES) {
    return "Attachments can be at most 25 MB in total";
  }
  return null;
}

/** "1.2 MB", "340 KB". */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
